/**
 * ConfirmDialog — the dashboard's confirmation prompt.
 * ───────────────────────────────────────────────────────────────────────────
 * Replaces `window.confirm`. Two reasons that is not a style preference:
 *
 *   1. The native dialog is chrome, not product. It says
 *      "localhost:3000 says" above your words, renders in the OS font on a
 *      grey slab, cannot show which brand or file is about to be destroyed,
 *      and looks identical whether the next click deletes a logo or empties a
 *      catalogue.
 *   2. It BLOCKS the main thread. Nothing can render behind it, so the button
 *      that opened it cannot show a spinner, and a long delete leaves the page
 *      frozen with no sign it is working.
 *
 * The shape here is lifted from the one good in-app confirmation this codebase
 * already had — the media-delete modal inside EditRetailerWholesalerForm —
 * generalised so there is one of these rather than a new one per form.
 *
 * Controlled, not imperative: no promise-returning `confirm()` helper. Callers
 * already hold state for what they are about to do, and a hook that resolves a
 * promise hides the busy flag exactly where it matters most.
 *
 *   const [confirm, setConfirm] = useState(null);
 *   …
 *   <ConfirmDialog
 *     show={!!confirm}
 *     tone="danger"
 *     title="Delete logo?"
 *     body="This permanently removes the logo from the brand and from Cloudinary."
 *     confirmLabel="Delete logo"
 *     busy={removing}
 *     onConfirm={reallyDelete}
 *     onCancel={() => setConfirm(null)}
 *   />
 */
import React, { useCallback, useMemo, useState } from "react";
import { Button, Modal, Spinner } from "react-bootstrap";

/**
 * useConfirmDialog — the state a `window.confirm` call site needs to become a
 * ConfirmDialog, in one line.
 *
 *   const confirmDialog = useConfirmDialog();
 *   ...
 *   confirmDialog.ask({
 *     title: "Reactivate your shop?",
 *     body: "It becomes visible to customers again.",
 *     tone: "success",
 *     confirmLabel: "Reactivate",
 *     onConfirm: async () => { ...the code that used to follow the confirm },
 *   });
 *   ...
 *   <ConfirmDialog {...confirmDialog.props} />
 *
 * `onConfirm` runs behind the dialog's busy state, so the operator sees a
 * spinner instead of a frozen page, and the dialog closes when it settles
 * (an error is the caller's to toast, as before). Still controlled, still
 * one dialog per page; this only holds the "what did they click" state that
 * every call site otherwise re-invents.
 */
export const useConfirmDialog = () => {
  const [pending, setPending] = useState(null);
  const [busy, setBusy] = useState(false);

  const ask = useCallback((options) => {
    setPending(options || null);
  }, []);

  const cancel = useCallback(() => {
    if (busy) return;
    setPending(null);
  }, [busy]);

  const confirm = useCallback(async () => {
    if (!pending) return;
    setBusy(true);
    try {
      await pending.onConfirm?.();
    } finally {
      setBusy(false);
      setPending(null);
    }
  }, [pending]);

  const props = useMemo(
    () => ({
      show: !!pending,
      title: pending?.title,
      body: pending?.body,
      note: pending?.note,
      confirmLabel: pending?.confirmLabel,
      cancelLabel: pending?.cancelLabel,
      busyLabel: pending?.busyLabel,
      tone: pending?.tone || "danger",
      busy,
      onConfirm: confirm,
      onCancel: cancel,
    }),
    [pending, busy, confirm, cancel]
  );

  return { ask, props, isOpen: !!pending };
};

/* Bootstrap's own variants, chosen by what the confirm actually does.
   `danger` is reserved for destroying something — a deactivation hides a
   product, it does not delete it, so it gets `warning`, not red. Overusing red
   is how people stop reading it. */
const TONE = {
  danger: { variant: "danger", accent: "#b3261e" },
  warning: { variant: "warning", accent: "#a35b00" },
  primary: { variant: "primary", accent: "#17385a" },
  success: { variant: "success", accent: "#1e7e34" },
};

const ConfirmDialog = ({
  show,
  title,
  body,
  note,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  busyLabel,
  tone = "danger",
  busy = false,
  /* Greys out the confirm button while the operator still has to choose
     something inside `body` (MediaDeleteDialog with nothing ticked). */
  confirmDisabled = false,
  onConfirm,
  onCancel,
}) => {
  const { variant, accent } = TONE[tone] || TONE.danger;

  return (
    <Modal
      show={!!show}
      /* While the action is running the dialog cannot be dismissed — closing it
         mid-delete leaves the operator with no idea whether it finished. */
      onHide={busy ? undefined : onCancel}
      centered
      backdrop={busy ? "static" : true}
      keyboard={!busy}
    >
      <Modal.Header closeButton={!busy}>
        <Modal.Title style={{ textTransform: "none" }}
        >
          {title}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body style={{ fontFamily: "'Inter', sans-serif" }}>
        {typeof body === "string" ? (
          <p style={{ marginBottom: 0 }}>{body}</p>
        ) : (
          body
        )}
        {note && (
          <p
            className="text-muted"
            style={{ fontSize: 13, marginTop: 8, marginBottom: 0 }}
          >
            {note}
          </p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant={variant}
          onClick={onConfirm}
          disabled={busy || confirmDisabled}
        >
          {busy ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {busyLabel || "Working…"}
            </>
          ) : (
            confirmLabel
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ConfirmDialog;
